// dashboard/src/hooks/useTenant.tsx
// Provides tenant context for multi-tenant data isolation

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth'
import { useAuth, getAuthToken } from './useAuth'
import { logTenant, logAPIError } from '../utils/debugLogger'
import { isCognitoConfigured } from '@/config/aws'
import { API_BASE } from '@/services/api'

interface TenantContextType {
  tenantId: string | null
  originalTenantId: string | null
  isSwitched: boolean
  isLoading: boolean
  error: string | null
  ensureTenantExists: () => Promise<boolean>
  switchTenant: (tenantId: string) => void
  resetTenant: () => void
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

// Global tenant ID for synchronous access in API calls
let currentTenantId: string | null = null

export function setCurrentTenantId(tenantId: string | null) {
  currentTenantId = tenantId
}

export function getCurrentTenantId(): string | null {
  return currentTenantId
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth()
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [originalTenantId, setOriginalTenantId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tenantChecked, setTenantChecked] = useState(false)

  const switchTenant = useCallback((newTenantId: string) => {
    if (!originalTenantId) {
      setOriginalTenantId(tenantId)
    }
    setTenantId(newTenantId)
    setCurrentTenantId(newTenantId)
  }, [tenantId, originalTenantId])

  const resetTenant = useCallback(() => {
    if (originalTenantId) {
      setTenantId(originalTenantId)
      setCurrentTenantId(originalTenantId)
      setOriginalTenantId(null)
    }
  }, [originalTenantId])

  // Auto-create tenant if it doesn't exist
  const createTenantIfNotExists = useCallback(async (tid: string, email: string, name: string) => {
    try {
      const token = await getAuthToken()

      // First, check if tenant exists
      const checkResponse = await fetch(`${API_BASE}/v1/tenants/${tid}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tid,
          ...(token && token !== 'demo-token' ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      })

      if (checkResponse.ok) {
        logTenant(`Tenant ${tid} already exists`)
        return true
      }

      if (checkResponse.status === 404) {
        // Tenant doesn't exist, create it
        logTenant(`Creating new tenant: ${tid}`)

        const createResponse = await fetch(`${API_BASE}/v1/tenants`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Id': tid,
            ...(token && token !== 'demo-token' ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            tenant_id: tid,
            name: name || email.split('@')[0],
            admin_email: email,
            plan: 'trial',
          }),
        })

        if (createResponse.ok || createResponse.status === 201) {
          logTenant(`Tenant ${tid} created successfully`)
          return true
        } else if (createResponse.status === 409) {
          // Tenant already exists (race condition)
          logTenant(`Tenant ${tid} already exists (409)`)
          return true
        } else {
          const errorData = await createResponse.json().catch(() => ({}))
          logAPIError(`Failed to create tenant: ${tid}`, errorData)
          return false
        }
      }

      return false
    } catch (err) {
      logAPIError('Error checking/creating tenant', err)
      return false
    }
  }, [])

  const ensureTenantExists = useCallback(async () => {
    if (!tenantId || !user?.email) return false
    return createTenantIfNotExists(tenantId, user.email, user.name)
  }, [tenantId, user, createTenantIfNotExists])

  useEffect(() => {
    if (authLoading) {
      return
    }

    if (!isAuthenticated || !user) {
      setTenantId(null)
      setCurrentTenantId(null)
      setIsLoading(false)
      setTenantChecked(false)
      return
    }

    // Tenant ID must come from Cognito custom:tenant_id (set by PostConfirmation trigger).
    // We do NOT generate tenant IDs from email since they include a random suffix.
    if (user.tenantId) {
      setTenantId(user.tenantId)
      setCurrentTenantId(user.tenantId)
      logTenant(`Using tenant_id: ${user.tenantId} for user: ${user.email}`, {
        source: 'cognito',
        email: user.email,
      })
      setIsLoading(false)
      return
    }

    // If tenant_id not in token yet, force a session refresh.
    // The PostConfirmation trigger sets custom:tenant_id before login,
    // but the token might be stale (e.g., cached from a previous session).
    if (isCognitoConfigured()) {
      logTenant('tenant_id not found in token, refreshing session...')
      ;(async () => {
        try {
          await fetchAuthSession({ forceRefresh: true })
          const attributes = await fetchUserAttributes()
          const refreshedTid = attributes['custom:tenant_id']
          if (refreshedTid) {
            setTenantId(refreshedTid)
            setCurrentTenantId(refreshedTid)
            logTenant(`Resolved tenant_id after refresh: ${refreshedTid}`)
          } else {
            // Fallback: resolve tenant by email via API
            // Supports users who signed up before custom:tenant_id was set in Cognito
            logTenant('tenant_id not in Cognito attributes, resolving by email...')
            try {
              const token = await getAuthToken()
              const resolveResponse = await fetch(
                `${API_BASE}/v1/tenants/resolve?email=${encodeURIComponent(user.email)}`,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    ...(token && token !== 'demo-token' ? { 'Authorization': `Bearer ${token}` } : {}),
                  },
                }
              )
              if (resolveResponse.ok) {
                const data = await resolveResponse.json()
                if (data.tenant_id) {
                  setTenantId(data.tenant_id)
                  setCurrentTenantId(data.tenant_id)
                  logTenant(`Resolved tenant_id from API: ${data.tenant_id}`)
                } else {
                  setError('Could not determine tenant ID. Please log out and log back in.')
                  logAPIError('tenant_id not found in resolve response', { user })
                }
              } else {
                setError('Could not determine tenant ID. Please log out and log back in.')
                logAPIError('tenant resolve API failed', { status: resolveResponse.status, user })
              }
            } catch (resolveErr) {
              setError('Could not determine tenant ID. Please log out and log back in.')
              logAPIError('Error resolving tenant by email', resolveErr)
            }
          }
        } catch (err) {
          setError('Could not determine tenant ID. Please log out and log back in.')
          logAPIError('Error refreshing session for tenant_id', err)
        } finally {
          setIsLoading(false)
        }
      })()
    } else {
      // Demo mode - no Cognito
      setError('Could not determine tenant ID')
      logAPIError('Could not determine tenant ID (demo mode)', { user })
      setIsLoading(false)
    }
  }, [user, isAuthenticated, authLoading])

  // Auto-create tenant when tenant ID is set
  useEffect(() => {
    if (tenantId && user?.email && !tenantChecked && !isLoading) {
      setTenantChecked(true)
      createTenantIfNotExists(tenantId, user.email, user.name).catch(console.error)
    }
  }, [tenantId, user, tenantChecked, isLoading, createTenantIfNotExists])

  const value: TenantContextType = {
    tenantId,
    originalTenantId,
    isSwitched: originalTenantId !== null,
    isLoading: isLoading || authLoading,
    error,
    ensureTenantExists,
    switchTenant,
    resetTenant,
  }

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant() {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}
